import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { Button } from '../button';
import { Icon } from '../icon';

import {
  StyledTooltip,
  TooltipV2Container,
  TooltipBubble,
  TooltipContent,
  TooltipArrow,
  TooltipText,
  TooltipIconContainer,
  TooltipActions,
} from './styles';

import { ICONS } from '@controlrooms/constants';
import { unique } from '@controlrooms/utils';

interface TooltipProps {
  className?: string;
  children: React.ReactNode;
  delay?: string;
  label: string | React.ReactElement;
  offset?: string;
  place?: string;
  type?: 'primary' | 'secondary';
  testid?: string;
  theme?: string;
  // V2 props
  version?: 'v1' | 'v2';
  direction?: 'right' | 'left' | 'up' | 'down';
  tooltipType?: 'text' | 'actionable';
  prefixIcon?: boolean;
  prefixIconName?: typeof ICONS[keyof typeof ICONS];
  onActionClick?: () => void;
  actionIconName?: typeof ICONS[keyof typeof ICONS];
}

export const Tooltip = React.forwardRef<HTMLSpanElement, TooltipProps>(
  (
    {
      className,
      children,
      delay,
      label,
      offset,
      place,
      type = 'primary',
      testid,
      theme,
      version = 'v1',
      direction = 'right',
      tooltipType = 'text',
      prefixIcon = false,
      prefixIconName = ICONS.Scissor,
      onActionClick,
      actionIconName = ICONS.Gear,
    },
    ref,
  ) => {
    const _label = typeof label === 'string' ? label : ReactDOMServer.renderToString(label);
    const id = unique();

    // Legacy V1 implementation
    if (version === 'v1') {
      return (
        <>
          <ReactTooltip id={id} />
          <StyledTooltip
            data-tip={_label}
            data-testid={testid}
            data-for={id}
            data-html={typeof label !== 'string'}
            delayShow={delay}
            className={className}
            data-offset={offset}
            data-place={place}
            type={type}
            _theme={theme}
            ref={ref}
          >
            {children}
          </StyledTooltip>
        </>
      );
    }

    // New V2 implementation
    return (
      <TooltipV2Container className={className} data-testid={testid} ref={ref}>
        {children}
        <TooltipBubble direction={direction} tooltipType={tooltipType}>
          <TooltipArrow direction={direction} />
          <TooltipContent>
            {prefixIcon && (
              <TooltipIconContainer>
                <Icon name={prefixIconName} width="14" height="14" />
              </TooltipIconContainer>
            )}
            <TooltipText tooltipType={tooltipType}>{label}</TooltipText>
            {tooltipType === 'actionable' && onActionClick && (
              <TooltipActions>
                <Button
                  buttonType="icon"
                  buttonSize="small"
                  iconName={actionIconName}
                  onClick={onActionClick}
                />
              </TooltipActions>
            )}
          </TooltipContent>
        </TooltipBubble>
      </TooltipV2Container>
    );
  },
);
